'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { BridgeApi, useDidUpdate } from '@skin-seoul/utils';

const QUANTITY_IDLE_MS = 500;
const REMOVE_ITEM_ERROR = 'Could not remove this item. Please try again.';
const UPDATE_QUANTITY_ERROR = 'We couldn’t update your quantity. Please try again.';

interface QueuedBatch {
  promise: Promise<boolean>;
  resolve: (success: boolean) => void;
}

interface RemoveItemCallbacks {
  onSuccess: () => void;
  onError: () => void;
}

interface UseCartQuantityQueueOptions {
  /** Changing it drops queued edits, clears errors and detaches results of in-flight writes. */
  scope: string;
  items: ReadonlyArray<{ key: string; quantity: number }>;
  updateItem: (itemKey: string, quantity: number) => Promise<unknown>;
  removeItem: (itemKey: string, callbacks: RemoveItemCallbacks) => void;
}

/**
 * Coalesces quantity edits per line, writes them serially after an idle pause, and lets
 * checkout wait for every cart write it is told about (including discovery adds).
 */
export function useCartQuantityQueue({ scope, items, updateItem, removeItem }: UseCartQuantityQueueOptions) {
  const [resetVersion, setResetVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasQueued, setHasQueued] = useState(false);
  const [pendingUpdates] = useState(() => new Set<Promise<boolean>>());
  const [mutationGenerations] = useState(() => new WeakMap<Promise<boolean>, number>());
  const [queuedQuantities] = useState(() => new Map<string, number>());
  const scopeGeneration = useRef(0);
  const checkoutWaiting = useRef(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queuedBatch = useRef<QueuedBatch | null>(null);
  const isCurrentScope = (generation: number) => generation === scopeGeneration.current;

  const finishQueuedBatch = useCallback(
    (success: boolean) => {
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
      idleTimer.current = null;
      queuedQuantities.clear();
      setHasQueued(false);
      const batch = queuedBatch.current;
      queuedBatch.current = null;
      if (batch) {
        pendingUpdates.delete(batch.promise);
        batch.resolve(success);
      }
    },
    [pendingUpdates, queuedQuantities],
  );

  // Detach in-flight results and drop queued edits when the scope changes or the view unmounts.
  useEffect(
    () => () => {
      scopeGeneration.current += 1;
      finishQueuedBatch(false);
    },
    [scope, finishQueuedBatch],
  );
  // A fresh mount already starts from this state; only scope updates need the reset.
  useDidUpdate(() => {
    setResetVersion((version) => version + 1);
    setError(null);
    setIsUpdating(false);
    checkoutWaiting.current = false;
  }, [scope]);

  const trackMutation = (update: Promise<boolean>) => {
    const generation = scopeGeneration.current;
    setIsUpdating(true);
    pendingUpdates.add(update);
    mutationGenerations.set(update, generation);
    void update.then(() => {
      pendingUpdates.delete(update);
      if (isCurrentScope(generation)) {
        setIsUpdating(pendingUpdates.size > 0);
      }
    });
  };

  const writeQueuedQuantities = async (
    updates: Array<[itemKey: string, quantity: number]>,
    earlierUpdates: Array<Promise<boolean>>,
    generation: number,
  ) => {
    try {
      if (earlierUpdates.length) {
        const results = await Promise.all(
          earlierUpdates.map(async (pending) => {
            const success = await pending;
            return mutationGenerations.get(pending) !== generation || success;
          }),
        );
        if (!isCurrentScope(generation)) {
          return false;
        }
        if (results.some((success) => !success)) {
          setResetVersion((version) => version + 1);
          return false;
        }
      }
      for (const [itemKey, quantity] of updates) {
        if (!isCurrentScope(generation)) {
          return false;
        }
        await updateItem(itemKey, quantity);
        if (!isCurrentScope(generation)) {
          return false;
        }
      }
      if (BridgeApi.isWebview()) {
        BridgeApi.cartUpdated();
      }
      return true;
    } catch {
      if (!isCurrentScope(generation)) {
        return false;
      }
      setError(UPDATE_QUANTITY_ERROR);
      setResetVersion((version) => version + 1);
      return false;
    }
  };

  const flush = () => {
    if (!queuedBatch.current) {
      return;
    }
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }
    idleTimer.current = null;
    const generation = scopeGeneration.current;
    const batch = queuedBatch.current;
    const updates = [...queuedQuantities];
    queuedBatch.current = null;
    queuedQuantities.clear();
    setHasQueued(false);
    setIsUpdating(true);
    // Serialize lines because each response replaces the whole cart cache.
    const earlierUpdates = [...pendingUpdates].filter((pending) => pending !== batch.promise);
    const update = writeQueuedQuantities(updates, earlierUpdates, generation);
    void update.then((success) => {
      pendingUpdates.delete(batch.promise);
      batch.resolve(success);
    });
    trackMutation(update);
  };

  const schedule = (itemKey: string, quantity: number) => {
    setError(null);
    if (!queuedBatch.current) {
      let resolve!: (success: boolean) => void;
      const promise = new Promise<boolean>((done) => {
        resolve = done;
      });
      queuedBatch.current = { promise, resolve };
      pendingUpdates.add(promise);
      mutationGenerations.set(promise, scopeGeneration.current);
    }
    if (items.find((item) => item.key === itemKey)?.quantity === quantity) {
      queuedQuantities.delete(itemKey);
    } else {
      queuedQuantities.set(itemKey, quantity);
    }
    if (!queuedQuantities.size) {
      finishQueuedBatch(true);
      return;
    }
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }
    setHasQueued(true);
    idleTimer.current = setTimeout(flush, QUANTITY_IDLE_MS);
  };

  /** The quantity waiting to be written for a line, if the line has a queued edit. */
  const getQueuedQuantity = (itemKey: string) => queuedQuantities.get(itemKey);

  /** Starts a removal unless a write is running or edits are queued; returns whether it started. */
  const remove = (itemKey: string) => {
    if (isUpdating || queuedBatch.current) {
      return false;
    }
    queuedQuantities.delete(itemKey);
    if (!queuedQuantities.size) {
      finishQueuedBatch(false);
    }
    const generation = scopeGeneration.current;
    setError(null);
    const removal = new Promise<boolean>((resolve) => {
      removeItem(itemKey, {
        onSuccess: () => {
          if (BridgeApi.isWebview()) {
            BridgeApi.cartUpdated();
          }
          resolve(true);
        },
        onError: () => {
          if (isCurrentScope(generation)) {
            setError(REMOVE_ITEM_ERROR);
          }
          resolve(false);
        },
      });
    });
    trackMutation(removal);
    return true;
  };

  /**
   * Flushes queued edits. Returns null when checkout can proceed now; otherwise a promise that
   * resolves whether every in-flight write of this scope succeeded (false while already waiting).
   */
  const settleBeforeCheckout = (): Promise<boolean> | null => {
    flush();
    if (checkoutWaiting.current) {
      return Promise.resolve(false);
    }
    if (pendingUpdates.size === 0) {
      return null;
    }
    checkoutWaiting.current = true;
    const generation = scopeGeneration.current;
    return Promise.all(
      [...pendingUpdates].map(async (pending) => {
        const success = await pending;
        return mutationGenerations.get(pending) !== generation || success;
      }),
    ).then((results) => {
      if (!isCurrentScope(generation)) {
        return false;
      }
      checkoutWaiting.current = false;
      return results.every(Boolean);
    });
  };

  return {
    resetVersion,
    error,
    isUpdating,
    hasQueued,
    schedule,
    getQueuedQuantity,
    remove,
    trackMutation,
    settleBeforeCheckout,
  };
}
